import api from './api';

// ─────────────────────────────────────────────────────────────────────────
// Notification API
// Base path: /api/notifications  (auth + X-Tenant-ID auto-attached by `api`)
// ─────────────────────────────────────────────────────────────────────────

/**
 * GET /api/notifications/unread-count
 * Lightweight endpoint for the Navbar bell badge. Poll this, not getFeed().
 * Returns: number
 */
export const getUnreadCount = async () => {
  const { data } = await api.get('/notifications/unread-count');
  return data.unreadCount ?? 0;
};

/**
 * GET /api/notifications?page=0&size=20&category=APPOINTMENT
 * category is optional — omit or pass "ALL" for every category.
 * Returns: { notifications[], unreadCount, totalPages, totalElements }
 */
export const getNotificationFeed = async (page = 0, size = 20, category) => {
  const params = { page, size };
  if (category && category !== 'ALL') params.category = category;
  const { data } = await api.get('/notifications', { params });
  return data;
};

/**
 * POST /api/notifications/{id}/read
 * Marks a single notification as read.
 */
export const markNotificationRead = async (id) => {
  await api.post(`/notifications/${id}/read`);
};

/**
 * POST /api/notifications/read-all
 * Marks every unread notification for the current user as read.
 */
export const markAllNotificationsRead = async () => {
  await api.post('/notifications/read-all');
};

// ─────────────────────────────────────────────────────────────────────────
// Recent Activity (dashboard cards — Patient / Doctor / Admin)
// ─────────────────────────────────────────────────────────────────────────

const ADMIN_ROLES = ['ADMIN', 'SUPER_ADMIN', 'SYSTEM_ADMIN'];

/**
 * GET /api/notifications/recent?limit=5   — patient / doctor (scoped to caller)
 * GET /api/admin/audit/recent?limit=10    — admin only (tenant-wide, 403 otherwise)
 *
 * One shared call for all three dashboard cards — role decides which endpoint
 * is hit. Both return the same shape: [{ id, text, colorTag, timeAgo, createdAt }]
 * Do not cache this response — timeAgo is computed server-side per request.
 */
export const getRecentActivity = async (role, limit) => {
  const isAdmin = ADMIN_ROLES.includes((role || '').toUpperCase());
  const url = isAdmin ? '/admin/audit/recent' : '/notifications/recent';
  const { data } = await api.get(url, { params: { limit: limit ?? (isAdmin ? 10 : 5) } });
  return data;
};